import React from "react";
import Head from "next/head";
import Link from "next/link";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Sparkles,
  Shield,
  Zap,
  Loader2,
  Plus,
  Check,
  MessageSquare,
} from "lucide-react";
import { ToolBackControl } from "@/components/nav/ToolBackControl";
import { Button } from "@/components/ui/Button";
import RelatedGuides from "@/components/seo/RelatedGuides";
import { useAuth } from "@/lib/supabase/AuthContext";
// LexiconQuickWidget removed

export default function DurmahLexiconArticle() {
  const { user } = useAuth();
  const glossaryHref = user ? "/study/glossary" : "/signup?next=/study/glossary";
  const dashboardHref = user ? "/dashboard" : "/signup?next=/dashboard";

  return (
    <div className="bg-white dark:bg-[#0B1412] min-h-screen flex flex-col transition-colors duration-500">
      <Head>
        <title>Durmah Lexicon™ | Mastering Legal Language | Durmah</title>
        <meta
          name="description"
          content="Master the difficult legal terminology with Durmah Lexicon™. Learn why attending live lectures is essential for legal training."
        />
        <meta
          name="keywords"
          content="legal lexicon, law terminology, courtroom language, legal education, Durham Law, Durmah"
        />
        <link key="route-canonical"
          rel="canonical"
          href="https://www.durmah.ai/articles/durmah-lexicon"
        />
      </Head>

      <main className="flex-1">
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="mb-8">
            <ToolBackControl fallback="/resources" label="Back to Hub" />
          </div>

          <div className="prose prose-indigo dark:prose-invert max-w-none">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D5BF76]/20 text-[#D5BF76] text-[10px] font-black uppercase tracking-widest border border-[#D5BF76]/30 mb-6">
              <BookOpen className="w-3.5 h-3.5" />
              <span>Durmah ORIGINAL</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-black text-gray-900 dark:text-white mb-6 tracking-tight leading-tight">
              Mastering Legal Language: <br />
              The Durmah <span className="text-[#D5BF76]">Lexicon™</span>
            </h1>

            <p className="text-xl text-gray-600 dark:text-teal-100/70 leading-relaxed mb-12 font-medium">
              Studying law is, in large part, learning a new language. If half of
              every sentence is unfamiliar, reading is slow and exhausting. The
              Durmah Lexicon is built to fix that, so you can not only read the
              law but use its vocabulary with confidence.
            </p>

            <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white mb-6">
              Why Language is the Lawyer's Primary Tool
            </h2>

            <p className="text-lg leading-relaxed mb-8">
              In practice, clarity is power: a single misunderstood term can
              change how an argument lands. For students, the sheer volume of
              Latinate terms, archaic phrasing, and precise technical meanings is
              one of the biggest hurdles in the first year or two. That is not
              because you are not capable, but because nobody learns a new
              vocabulary overnight.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-12">
              <div className="p-6 bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/10">
                <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#D5BF76]" />
                  Interactive Mastery
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  We've isolated the most difficult legal terminology from your
                  specific curriculum into an interactive, memory-optimised
                  glossary.
                </p>
              </div>
              <div className="p-6 bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/10">
                <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-indigo-500" />
                  Contextual Learning
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Each term is defined and then shown in the context of your
                  lectures, which makes it easier to recall in exams.
                </p>
              </div>
            </div>

            {/* INTERACTIVE WIDGET SHOWCASE REMOVED */}
            <div className="my-16 not-prose">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-4 tracking-tight">
                  Now Integrated into Your Workflow
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto font-medium">
                  The Lexicon is now always available in your global header.
                  Click "Open Lexicon" or press Cmd+K anywhere.
                </p>
              </div>
              {/* Static Showcase Image could go here, or just leave text */}
              <div className="relative w-full h-48 bg-gray-100 dark:bg-white/5 rounded-3xl border border-gray-200 dark:border-white/10 flex items-center justify-center">
                <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">
                  Widget now in Header
                </p>
              </div>
            </div>

            {/* ETHICAL SECTION */}
            <div className="not-prose my-16 p-8 md:p-12 bg-[#123733] rounded-[2.5rem] border border-[#D5BF76]/30 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#D5BF76]/10 rounded-full blur-3xl -mr-32 -mt-32"></div>
              <div className="relative z-10">
                <div className="flex items-center gap-3 text-[#D5BF76] mb-6">
                  <Shield className="w-8 h-8" />
                  <span className="text-xs font-black uppercase tracking-[0.2em]">
                    Academic Ethics & Professionalism
                  </span>
                </div>

                <h2 className="text-3xl md:text-4xl font-black text-white mb-8 tracking-tight">
                  The Importance of the Live Lecture
                </h2>

                <div className="space-y-6 text-teal-50/90 text-lg leading-relaxed">
                  <p>
                    The Lexicon gives you the building blocks of legal language,
                    but it is worth being clear: <strong>it is a companion, not
                    a replacement for the lecture hall.</strong>
                  </p>

                  <p>
                    Attending live lectures is a fundamental part of your legal
                    training. Sitting in a crowded lecture hall, surrounded by
                    hundreds of your peers, is a shared intellectual experience
                    that cannot be replicated behind a computer screen.
                  </p>

                  <div className="bg-white/5 p-6 rounded-2xl border border-white/10 space-y-4">
                    <p className="font-bold text-white uppercase tracking-wider text-xs">
                      Why Attendance Matters:
                    </p>
                    <ul className="list-disc pl-5 space-y-3 text-sm">
                      <li>
                        <strong>Real-world focus</strong>: learning to
                        concentrate in a busy, distracting room is a skill you
                        will need long after university.
                      </li>
                      <li>
                        <strong>Professional stamina</strong>: showing up and
                        staying engaged builds the focus that professional life
                        later demands.
                      </li>
                      <li>
                        <strong>Nuance &amp; rhythm</strong>: hearing a lecturer
                        deliver an argument live captures the emphasis and pacing
                        of legal reasoning that text on its own cannot.
                      </li>
                    </ul>
                  </div>

                  <p className="text-sm text-[#D5BF76]/80 pt-4">
                    <span className="font-semibold">Durmah principle:</span>{" "}
                    The courtroom is a live environment, not a silent study
                    space. Attending lectures and practising with legal language
                    can help you become more comfortable using it aloud.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white mb-6">
              How to Use the Lexicon Effectively
            </h2>

            <p className="mb-8 font-medium">
              The Lexicon works best as a way to <strong>reinforce</strong> what
              you heard in the lecture hall. If a term flew past you during a
              session, it is your recovery tool, so a moment of confusion does
              not turn into a fortnight of it.
            </p>


            <ul className="list-none p-0 space-y-4 mb-12">
              <li className="flex gap-4 items-start">
                <div className="w-6 h-6 rounded-full bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center shrink-0 mt-1">
                  <div className="w-2 h-2 rounded-full bg-teal-600"></div>
                </div>
                <div>
                  <span className="font-bold text-gray-900 dark:text-white">
                    Pre-Read Terminology:
                  </span>
                  <p className="text-gray-600 dark:text-gray-400 mt-1">
                    Scan relevant Lexicon terms before a lecture to prime your
                    brain for faster comprehension.
                  </p>
                </div>
              </li>
              <li className="flex gap-4 items-start">
                <div className="w-6 h-6 rounded-full bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center shrink-0 mt-1">
                  <div className="w-2 h-2 rounded-full bg-teal-600"></div>
                </div>
                <div>
                  <span className="font-bold text-gray-900 dark:text-white">
                    Post-Lecture Clarification:
                  </span>
                  <p className="text-gray-600 dark:text-gray-400 mt-1">
                    Use the search function immediately after a lecture to lock
                    in any terms that felt "fuzzy."
                  </p>
                </div>
              </li>
            </ul>

            <div className="py-12 border-t border-gray-100 dark:border-white/5">
              <Link href="/articles/syllabus-shield" className="group block">
                <div className="flex items-center justify-between p-8 rounded-3xl bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 transition-all border border-transparent hover:border-[#D5BF76]/20">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#D5BF76] mb-2 block">
                      Next Guide
                    </span>
                    <h4 className="text-2xl font-black text-gray-900 dark:text-white group-hover:text-[#D5BF76] transition-colors">
                      SyllabusShield™: No Content Left Behind
                    </h4>
                  </div>
                  <ArrowRight className="w-8 h-8 text-gray-300 group-hover:text-[#D5BF76] transition-all transform group-hover:translate-x-2" />
                </div>
              </Link>
            </div>
          </div>

          <RelatedGuides
            currentSlug="durmah-lexicon"
            categories={["Productivity", "Ethics"]}
            pinnedSlugs={["no-question-is-a-stupid-question"]}
          />

          {/* CTA Block */}
          <div className="mt-20 p-8 md:p-12 bg-gradient-to-br from-[#123733] to-[#0B1412] rounded-[2.5rem] border border-[#D5BF76]/20 text-center text-white shadow-2xl">
            <h2 className="text-2xl md:text-4xl font-black mb-4">
              Step Into the <span className="text-[#D5BF76]">Lexicon</span>
            </h2>
            <p className="text-lg text-teal-100/60 mb-8 max-w-2xl mx-auto font-medium">
              Start mastering the language of the law today. Accessible in your
              student dashboard.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href={glossaryHref}>
                <Button className="bg-[#D5BF76] text-[#123733] hover:brightness-110 px-8 py-6 text-lg w-full sm:w-auto font-black rounded-2xl shadow-lg border-none">
                  Open Lexicon Hub
                </Button>
              </Link>
              <Link href={dashboardHref}>
                <Button
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 px-8 py-6 text-lg w-full sm:w-auto font-black rounded-2xl backdrop-blur-sm"
                >
                  Student Dashboard
                </Button>
              </Link>
            </div>
          </div>
        </article>
      </main>
    </div>
  );
}
